import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { orderAPI } from '../utils/api';
import { toast } from '../hooks/use-toast';
import { CreditCard, Truck, ShieldCheck } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

const CheckoutPage = ({ cart, onClearCart }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  const [customerInfo, setCustomerInfo] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: ''
  });

  const [shippingAddress, setShippingAddress] = useState({
    address_line1: '',
    address_line2: '',
    city: '',
    postal_code: '',
    country: 'Oman'
  });

  const [paymentMethod, setPaymentMethod] = useState('card');
  const [cardInfo, setCardInfo] = useState({
    card_number: '',
    card_name: '',
    expiry: '',
    cvv: ''
  });

  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);

  const subtotal = cart.reduce((sum, item) => sum + item.product.price * item.quantity, 0);
  const shipping = subtotal > 20 ? 0 : 2.5;
  const discount = appliedCoupon ? appliedCoupon.discount_amount : 0;
  const total = subtotal + shipping - discount;

  const handleCustomerInfoChange = (e) => {
    setCustomerInfo({ ...customerInfo, [e.target.name]: e.target.value });
  };

  const handleShippingChange = (e) => {
    setShippingAddress({ ...shippingAddress, [e.target.name]: e.target.value });
  };

  const handleCardChange = (e) => {
    setCardInfo({ ...cardInfo, [e.target.name]: e.target.value });
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      toast({
        title: t('common.error'),
        description: t('checkout.have_coupon'),
        variant: 'destructive',
      });
      return;
    }

    setCouponLoading(true);
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(
        `${BACKEND_URL}/api/coupons/validate/${couponCode}?subtotal=${subtotal}`
      );
      
      const data = await response.json();
      
      if (data.valid) {
        setAppliedCoupon(data);
        toast({
          title: t('common.success'),
          description: data.message,
        });
      } else {
        toast({
          title: t('common.error'),
          description: data.message,
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: t('common.error'),
        description: t('common.error'),
        variant: 'destructive',
      });
    } finally {
      setCouponLoading(false);
    }
  };

  const handleRemoveCoupon = () => {
    setCouponCode('');
    setAppliedCoupon(null);
  };

  const validateStep1 = () => {
    if (!customerInfo.first_name || !customerInfo.last_name || !customerInfo.email || !customerInfo.phone) {
      toast({
        title: t('common.error'),
        description: t('checkout.customer_info'),
        variant: 'destructive',
      });
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!shippingAddress.address_line1 || !shippingAddress.city || !shippingAddress.postal_code) {
      toast({
        title: t('common.error'),
        description: t('checkout.shipping_address'),
        variant: 'destructive',
      });
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (step === 1 && validateStep1()) {
      setStep(2);
    } else if (step === 2 && validateStep2()) {
      setStep(3);
    }
  };

  const handlePlaceOrder = async () => {
    if (paymentMethod === 'card') {
      if (!cardInfo.card_number || !cardInfo.card_name || !cardInfo.expiry || !cardInfo.cvv) {
        toast({
          title: t('common.error'),
          description: t('checkout.payment'),
          variant: 'destructive',
        });
        return;
      }
    }

    setLoading(true);

    try {
      const orderData = {
        customer_info: customerInfo,
        shipping_address: shippingAddress,
        items: cart.map(item => ({
          product_id: item.product.id,
          quantity: item.quantity
        })),
        coupon_code: appliedCoupon ? couponCode : null
      };

      const response = await orderAPI.createOrder(orderData);
      
      toast({
        title: t('common.success'),
        description: `#${response.data.order_number}`,
      });

      onClearCart();
      navigate(`/order-confirmation/${response.data.order_number}`);
    } catch (error) {
      toast({
        title: t('common.error'),
        description: t('common.error'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  if (cart.length === 0) {
    navigate('/cart');
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">{t('checkout.checkout')}</h1>

        {/* Progress Steps */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className={`flex items-center ${step >= 1 ? 'text-blue-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-300'}`}>
                1
              </div>
              <span className="ml-2 font-medium">{t('checkout.information')}</span>
            </div>
            <div className={`flex-1 h-1 mx-4 ${step >= 2 ? 'bg-blue-600' : 'bg-gray-300'}`} />
            <div className={`flex items-center ${step >= 2 ? 'text-blue-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-300'}`}>
                2
              </div>
              <span className="ml-2 font-medium">{t('checkout.shipping')}</span>
            </div>
            <div className={`flex-1 h-1 mx-4 ${step >= 3 ? 'bg-blue-600' : 'bg-gray-300'}`} />
            <div className={`flex items-center ${step >= 3 ? 'text-blue-600' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${step >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-300'}`}>
                3
              </div>
              <span className="ml-2 font-medium">{t('checkout.payment')}</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            {/* Step 1: Customer Information */}
            {step === 1 && (
              <Card>
                <CardHeader>
                  <CardTitle>{t('checkout.customer_info')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="first_name">{t('checkout.first_name')} *</Label>
                      <Input
                        id="first_name"
                        name="first_name"
                        value={customerInfo.first_name}
                        onChange={handleCustomerInfoChange}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="last_name">{t('checkout.last_name')} *</Label>
                      <Input
                        id="last_name"
                        name="last_name"
                        value={customerInfo.last_name}
                        onChange={handleCustomerInfoChange}
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="email">{t('checkout.email')} *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={customerInfo.email}
                      onChange={handleCustomerInfoChange}
                      required
                      dir={i18n.language === 'ar' ? 'ltr' : 'ltr'}
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">{t('checkout.phone')} *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={customerInfo.phone}
                      onChange={handleCustomerInfoChange}
                      required
                      dir={i18n.language === 'ar' ? 'ltr' : 'ltr'}
                    />
                  </div>
                  <Button onClick={handleNext} className="w-full">
                    {t('checkout.continue_to_shipping')}
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Step 2: Shipping Address */}
            {step === 2 && (
              <Card>
                <CardHeader>
                  <CardTitle>{t('checkout.shipping_address')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label htmlFor="address_line1">{t('checkout.address')} *</Label>
                    <Input
                      id="address_line1"
                      name="address_line1"
                      value={shippingAddress.address_line1}
                      onChange={handleShippingChange}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="address_line2">{t('checkout.address_line2')}</Label>
                    <Input
                      id="address_line2"
                      name="address_line2"
                      value={shippingAddress.address_line2}
                      onChange={handleShippingChange}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="city">{t('checkout.city')} *</Label>
                      <Input
                        id="city"
                        name="city"
                        value={shippingAddress.city}
                        onChange={handleShippingChange}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="postal_code">{t('checkout.postal_code')} *</Label>
                      <Input
                        id="postal_code"
                        name="postal_code"
                        value={shippingAddress.postal_code}
                        onChange={handleShippingChange}
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="country">{t('checkout.country')}</Label>
                    <Input
                      id="country"
                      name="country"
                      value={shippingAddress.country}
                      disabled
                    />
                  </div>
                  <div className="flex gap-4">
                    <Button variant="outline" onClick={() => setStep(1)} className="flex-1">
                      {t('checkout.back')}
                    </Button>
                    <Button onClick={handleNext} className="flex-1">
                      {t('checkout.continue_to_payment')}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Step 3: Payment */}
            {step === 3 && (
              <Card>
                <CardHeader>
                  <CardTitle>{t('checkout.payment_method')}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="card">{t('checkout.credit_card')}</SelectItem>
                      <SelectItem value="cod">{t('checkout.cash_on_delivery')}</SelectItem>
                    </SelectContent>
                  </Select>

                  {paymentMethod === 'card' && (
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="card_number">{t('checkout.card_number')} *</Label>
                        <Input
                          id="card_number"
                          name="card_number"
                          placeholder="1234 5678 9012 3456"
                          value={cardInfo.card_number}
                          onChange={handleCardChange}
                          maxLength={19}
                          dir={i18n.language === 'ar' ? 'ltr' : 'ltr'}
                        />
                      </div>
                      <div>
                        <Label htmlFor="card_name">{t('checkout.card_name')} *</Label>
                        <Input
                          id="card_name"
                          name="card_name"
                          value={cardInfo.card_name}
                          onChange={handleCardChange}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="expiry">{t('checkout.expiry')} *</Label>
                          <Input
                            id="expiry"
                            name="expiry"
                            placeholder="MM/YY"
                            value={cardInfo.expiry}
                            onChange={handleCardChange}
                            maxLength={5}
                            dir={i18n.language === 'ar' ? 'ltr' : 'ltr'}
                          />
                        </div>
                        <div>
                          <Label htmlFor="cvv">{t('checkout.cvv')} *</Label>
                          <Input
                            id="cvv"
                            name="cvv"
                            placeholder="123"
                            value={cardInfo.cvv}
                            onChange={handleCardChange}
                            maxLength={3}
                            dir={i18n.language === 'ar' ? 'ltr' : 'ltr'}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {paymentMethod === 'cod' && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <p className="text-sm text-blue-900">
                        Pay with cash when your order is delivered. Additional COD fee may apply.
                      </p>
                    </div>
                  )}

                  <div className="flex gap-4 mt-6">
                    <Button variant="outline" onClick={() => setStep(2)} className="flex-1">
                      {t('checkout.back')}
                    </Button>
                    <Button onClick={handlePlaceOrder} disabled={loading} className="flex-1 bg-green-600 hover:bg-green-700">
                      {loading ? t('checkout.processing') : t('checkout.place_order')}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Order Summary Sidebar */}
          <div className="lg:col-span-1">
            <Card className="sticky top-24">
              <CardHeader>
                <CardTitle>{t('cart.order_summary')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {cart.map((item, index) => (
                    <div key={index} className="flex gap-3">
                      <img
                        src={item.product.image}
                        alt={item.product.name}
                        className="w-16 h-16 object-cover rounded"
                      />
                      <div className="flex-1">
                        <p className="text-sm font-medium">{item.product.name}</p>
                        <p className="text-xs text-gray-600">{t('products.quantity')}: {item.quantity}</p>
                        <p className="text-sm font-semibold">OMR {(item.product.price * item.quantity).toFixed(2)}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <Separator className="my-4" />

                {/* Coupon Code Section */}
                <div className="space-y-2">
                  <Label htmlFor="coupon">{t('checkout.have_coupon')}</Label>
                  <div className="flex gap-2">
                    <Input
                      id="coupon"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      placeholder={t('cart.enter_code')}
                      disabled={appliedCoupon !== null}
                      className="flex-1"
                      dir={i18n.language === 'ar' ? 'ltr' : 'ltr'}
                    />
                    {appliedCoupon ? (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleRemoveCoupon}
                        className="whitespace-nowrap"
                      >
                        {t('checkout.remove')}
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        onClick={handleApplyCoupon}
                        disabled={couponLoading}
                        className="bg-orange-600 hover:bg-orange-700 whitespace-nowrap"
                      >
                        {couponLoading ? t('common.loading') : t('checkout.apply')}
                      </Button>
                    )}
                  </div>
                  {appliedCoupon && (
                    <p className="text-sm text-green-600 font-medium">
                      ✓ {appliedCoupon.message}
                    </p>
                  )}
                </div>

                <Separator className="my-4" />

                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>{t('cart.subtotal')}</span>
                    <span>OMR {subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>{t('cart.shipping')}</span>
                    <span>{shipping === 0 ? t('cart.free') : `OMR ${shipping.toFixed(2)}`}</span>
                  </div>
                  {appliedCoupon && (
                    <div className="flex justify-between text-sm text-green-600">
                      <span>{t('cart.discount')} ({couponCode})</span>
                      <span>-OMR {discount.toFixed(2)}</span>
                    </div>
                  )}
                  <Separator className="my-2" />
                  <div className="flex justify-between text-lg font-bold">
                    <span>{t('cart.total')}</span>
                    <span>OMR {total.toFixed(2)}</span>
                  </div>
                </div>

                <div className="mt-6 space-y-3 text-sm">
                  <div className="flex items-center gap-2 text-green-600">
                    <ShieldCheck className="h-4 w-4" />
                    <span>{t('checkout.secure_checkout')}</span>
                  </div>
                  <div className="flex items-center gap-2 text-blue-600">
                    <Truck className="h-4 w-4" />
                    <span>{t('checkout.free_shipping_info')}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckoutPage;
